import React from "react"
import { flatten, groupBy, map } from "lodash"
import { numberDayOrDateUpdate } from "Utils/dateUtils"
import {
  getFirstFieldUuidInData,
  getFirstFieldUuidInGroupBy,
} from "Components/WidgetSettingsComponent/widgetSettingsUtils.js"

export const getSourceFromNameAndCategory = (allSources, sourceName, sourceCategory) => {
  return allSources.find(source => {
    let hasSameSourceName = source?.name === sourceName
    let hasSameSourceCategory = source?.category === sourceCategory
    return hasSameSourceName && hasSameSourceCategory
  })
}

export const getSourceFromWidgetInfo = (widgetInfo, allSources, fieldsData) => {
  if (!widgetInfo || !Array.isArray(allSources) || !Array.isArray(fieldsData)) return {}

  const widgetFieldDataUuid = getFirstFieldUuidInData(widgetInfo)
  const widgetFieldGroupByUuid = getFirstFieldUuidInGroupBy(widgetInfo)

  const fieldDataAssociate = getFieldFromUUID(fieldsData, widgetFieldDataUuid)
  const fieldGroupByAssociate = getFieldFromUUID(fieldsData, widgetFieldGroupByUuid)
  if (!fieldDataAssociate || !fieldGroupByAssociate) return {}

  const sourceDataAssociate = allSources.find(
    source => source?.uuid === fieldDataAssociate?.dataSourceUUID
  )
  const sourceGroupByAssociate = allSources.find(
    source => source?.uuid === fieldGroupByAssociate?.dataSourceUUID
  )

  if (sourceDataAssociate?.uuid === sourceGroupByAssociate?.uuid) return sourceDataAssociate
  else return {}
}

export const getFieldFromUUID = (fieldsData, fieldUUID) => {
  if (!fieldsData || !Array.isArray(fieldsData) || fieldsData.length === 0) return undefined
  return fieldsData.find(field => field.uuid === fieldUUID)
}

export const getDefaultAggFunctionFromFieldType = fieldDataType => {
  return fieldDataType === "String" ? "count" : "sum"
}

export const getDefaultAggFunction = (selectionType, widgetInfo, fieldDataType) => {
  if (widgetInfo === undefined) return ""
  var finalFieldCalculus = getDefaultAggFunctionFromFieldType(fieldDataType)
  const isDataOrAdvancedData = selectionType === "data" || selectionType === "advancedData"
  const { widgetType, groupBy } = widgetInfo
  const noDependsOnField = !widgetInfo?.dependsOnField || widgetInfo?.dependsOnField.length === 0
  if (widgetType === "gaugeChart") {
    // finalFieldCalculus = "overallPercentage"
    finalFieldCalculus = ""
  } else if (widgetType === "pipeline") {
    finalFieldCalculus = "array_agg"
  } else if (widgetType === "fillingGauge" && fieldDataType === "String") {
    finalFieldCalculus = "count"
  } else if (widgetType === "fillingGauge" && fieldDataType === "Integer") {
    finalFieldCalculus = "sum"
  } else if (widgetType === "cloudWords") {
    finalFieldCalculus = "count"
  } else if (isDataOrAdvancedData && widgetType === "evolutionWidget") {
    finalFieldCalculus = "sum"
  } else if (isDataOrAdvancedData && groupBy.length === 0 && widgetType !== "multiGauge") {
    finalFieldCalculus = ""
  } else if (
    isDataOrAdvancedData &&
    widgetType === "multiGauge" &&
    groupBy.length === 0 &&
    noDependsOnField
  ) {
    finalFieldCalculus = ""
  }
  return finalFieldCalculus
}

export const getArrayIntersections = (array1 = [], array2 = []) => {
  var flattenArray1 = flatten(array1)
  var flattenArray2 = flatten(array2)
  return flattenArray1.filter(element => flattenArray2.includes(element))
}

export const getFieldsDisplayedButNotString = (fieldsData, UUIDList) => {
  var fieldsList = getFieldsListFromUUIDList(fieldsData, UUIDList)
  var filteredFieldList = keepFieldsDifferentFromString(fieldsList)
  return filteredFieldList
}

export const fieldsAreRelated = (field1, field2) => {
  if (field1 === undefined || field2 === undefined) return false
  var fieldSourceCategory1 = field1?.dataSource?.category
  var fieldSourceName1 = field1?.dataSource?.name
  var fieldRelations1 = field1?.relations

  var fieldSourceCategory2 = field2?.dataSource?.category
  var fieldSourceName2 = field2?.dataSource?.name
  var fieldRelations2 = field2?.relations

  var sourceCategoryAreEqual = fieldSourceCategory1 === fieldSourceCategory2
  var sourceNameAreEqual = fieldSourceName1 === fieldSourceName2
  var dataSourceAreEqual = sourceCategoryAreEqual && sourceNameAreEqual

  var relationIntersect = false
  if (fieldRelations1 && fieldRelations2) {
    var intersectionArray = getArrayIntersections(fieldRelations1, fieldRelations2)
    relationIntersect = Boolean(intersectionArray.length)
  }
  return dataSourceAreEqual || relationIntersect
}

export const getAllFieldsRelated = (fieldsData = [], fieldUUID) => {
  if (fieldsData.length === 0) return []
  var fieldSelected = getFieldFromUUID(fieldsData, fieldUUID)
  if (fieldSelected === undefined) return []
  var fieldsRelatedToReturn = fieldsData.filter(field => fieldsAreRelated(fieldSelected, field))
  return fieldsRelatedToReturn
}

export const getFieldsListFromUUIDList = (fieldsData = [], UUIDList = []) => {
  return UUIDList.map(uuid => {
    return getFieldFromUUID(fieldsData, uuid)
  })
}

export const keepFieldsDifferentFromString = fieldsList => {
  return fieldsList.filter(field => field !== undefined && field.dataType !== "String")
}

export const getFilteredFieldsData = (fieldsData, sourceFilterObj) => {
  if (!Array.isArray(fieldsData)) return []

  if (sourceFilterObj === undefined || Object.keys(sourceFilterObj).length === 0) {
    return fieldsData
  }

  var sourceFilterCategory = sourceFilterObj?.category
  var sourceFilterName = sourceFilterObj?.name
  var filterRelations = sourceFilterObj?.relations
  var authorizedFieldType = sourceFilterObj?.authorizedFieldType
  var unAuthorizedFieldType = sourceFilterObj?.unAuthorizedFieldType

  return fieldsData.filter(aField => {
    var { dataSource } = aField
    var { category, name } = dataSource

    if (dataSource === undefined) return false

    var { category, name } = dataSource

    //relation
    var isRelatedField = false
    if (filterRelations) {
      var intersectionArray = getArrayIntersections(filterRelations, aField?.relations)
      isRelatedField = Boolean(intersectionArray.length)
    }

    //source
    var isCorrectSourceCategory = [undefined, category].includes(sourceFilterCategory)
    var isCorrectSourceName = [undefined, name].includes(sourceFilterName)
    var isCorrectSource = isCorrectSourceCategory && isCorrectSourceName

    //dataType
    var isCorrectDataType = true
    if (authorizedFieldType && authorizedFieldType.length > 0) {
      isCorrectDataType = authorizedFieldType.includes(aField.dataType)
    }

    if (Array.isArray(unAuthorizedFieldType) && unAuthorizedFieldType.includes(aField.dataType)) {
      isCorrectDataType = false
    }

    return isCorrectDataType && (isCorrectSource || isRelatedField)
  })
}
// Regrouper chaque champs par Data source
export const groupFieldsBySourceData = (fieldsList, languageId) => {
  const groupByDataSource = map(groupBy(fieldsList, "source"), (item, index) => {
    return {
      name: index,
      data: item,
      dayLastUpdate: numberDayOrDateUpdate(item[0]?.last_update, 31, languageId),
    }
  })

  return groupByDataSource
}
